import axios from "axios";
import { API_BASE_URL, getAuthToken } from "./shared";
import { safeFileName } from "./studioUpload";

// API helpers for Track Administration (upload a track or clip).
const url = (path) => `${API_BASE_URL}/v1/artist-track/${path}`;
const config = (extra = {}) => ({
  headers: { token: getAuthToken(), "Content-Type": "application/json" },
  timeout: 20000,
  ...extra,
});
const result = (response) => response.data?.result;

export const trackApi = {
  rules: () => axios.get(url("rules"), config()).then(result),
  awards: () => axios.get(url("awards"), config()).then(result),
  list: () => axios.get(url("list"), config()).then(result),
  detail: (id) => axios.get(url(`detail?id=${encodeURIComponent(id)}`), config()).then(result),
  create: (body) => axios.post(url("create"), body, config()).then(result),
  update: (body) => axios.post(url("update"), body, config()).then(result),
  // Title and genre only, for a track that is already approved or live.
  updateLive: (body) => axios.post(url("update-live"), body, config()).then(result),
  licensingList: () => axios.get(url("licensing/list"), config()).then(result),
  // Dashboard page: headline numbers with month-on-month percentages, top tracks and recent activity (one call).
  dashboardSummary: (period = "28d") => axios.get(`${API_BASE_URL}/v1/artist-dashboard/summary`, config({ params: { period } })).then(result),
  // Per-track performance (FSD 6.3.3): series per day / week / month, totals, licences by class and the earnings split.
  performance: (trackId, period = "28d") => axios.get(`${API_BASE_URL}/v1/artist-dashboard/performance`, config({ params: { track_id: trackId, period } })).then(result),
  // Audience demographics and geography (FSD 6.3.4): groups under 50 listeners come back merged into "Other"; `enough` is false below 50 listeners.
  audience: (period = "28d", trackId) => axios.get(`${API_BASE_URL}/v1/artist-dashboard/audience`, config({ params: trackId ? { period, track_id: trackId } : { period } })).then(result),
  saveLicensing: (body) => axios.post(url("licensing/save"), body, config()).then(result),
  // Stops new Creator and Business licences for a live track (streaming and Personal are not affected). Safe to repeat.
  withdrawLicensing: (trackId) => axios.post(url("licensing/withdraw"), { track_id: trackId }, config()).then(result),
  // Entitlement usage log: params are track_id, usage_class, status, from, to (YYYY-MM-DD), cursor and limit.
  usageLog: (params = {}) => axios.get(url("licensing/usage-log"), config({ params })).then(result),
  // The same filters as a CSV. A small export is the file (200); a large one answers 202 and is emailed to the artist.
  exportUsageLog: (params = {}) =>
    axios.get(url("licensing/usage-log/export"), config({ params, responseType: "blob", timeout: 60000, validateStatus: (status) => status === 200 || status === 202 })),
  // Promote this sound: eligibility, the Ads wizard link and this track's campaigns with results (owner only).
  promotion: (id) => axios.get(url(`promotion?id=${encodeURIComponent(id)}`), config()).then(result),
  listAudioReplacements: () => axios.get(url("audio-replacement/list"), config()).then(result),
  reviewAudioReplacement: (body) => axios.post(url("audio-replacement/review"), body, config()).then(result),
  cancelAudioReplacement: (trackId) => axios.post(url("audio-replacement/cancel"), { track_id: trackId }, config()).then(result),
  attachMedia: (body) => axios.post(url("attach-media"), body, config()).then(result),
  uploadState: (body) => axios.post(url("upload-state"), body, config()).then(result),
  // Reading the file from storage can take a while, so processing and retry get a longer timeout.
  process: (trackId) => axios.post(url("process"), { track_id: trackId }, config({ timeout: 120000 })).then(result),
  retry: (trackId) => axios.post(url("retry"), { track_id: trackId }, config({ timeout: 120000 })).then(result),
  submit: (trackId) => axios.post(url("submit"), { track_id: trackId }, config()).then(result),
  takeDown: (trackId) => axios.post(url("take-down"), { track_id: trackId }, config()).then(result),
  // Moderation outcome: one appeal per track, after the one resubmission was also rejected. Evidence is [{ url, name, type, size }].
  appeal: (trackId, reason, evidence = []) => axios.post(url("appeal"), { track_id: trackId, reason, evidence }, config()).then(result),
  // Only for a track whose appeal was denied.
  remove: (trackId) => axios.post(url("delete"), { track_id: trackId }, config()).then(result),
  removeMedia: (trackId, mediaType) =>
    axios.post(url("remove-media"), { track_id: trackId, media_type: mediaType }, config()).then(result),
};

export async function loadGenres() {
  const response = await axios.post(
    `${API_BASE_URL}/v1/genre/list`,
    { filters: { status: "active" }, limit: 100 },
    config()
  );
  const body = response.data;
  const rows = body?.result?.data || body?.data || [];
  return rows.map((g) => ({ id: g.id, name: g.name }));
}

// ---- statuses ---------------------------------------------------------------------------------------------
// Keyed by the API's display_status.
export const STATUS_INFO = {
  draft: { label: "Draft", tone: "gray" },
  upload_pending: { label: "Upload pending", tone: "gray" },
  uploading: { label: "Uploading", tone: "blue" },
  uploaded: { label: "Uploaded", tone: "blue" },
  processing: { label: "Processing", tone: "blue" },
  ready_for_review: { label: "Ready for review", tone: "green" },
  pending_review: { label: "Under review", tone: "amber" },
  approved: { label: "Approved - not live yet", tone: "amber" }, // approved by review, but not on the public page until it is published
  published: { label: "Live", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  archived: { label: "Taken down", tone: "gray" },
  upload_failed: { label: "Upload failed", tone: "red" },
  processing_failed: { label: "Processing failed", tone: "red" },
};

export const TONE_CLASS = {
  gray: "bg-gray-100 text-gray-700",
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
};

export const statusInfo = (key) => STATUS_INFO[key] || { label: key || "Unknown", tone: "gray" };

// ---- friendly errors --------------------------------------------------------------------------------------
export const MESSAGES = {
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to upload tracks.",
  404: "This track could not be found.",
  500: "Something went wrong. Please try again later.",
  uploadFailed: "Upload failed. Please check your connection and try again.",
  processingFailed: "We couldn't process this audio file. Please try again or upload a different file.",
  title: "Please enter a track title.",
  unsupportedAudio: "This audio format is not supported. Please upload a supported audio file.",
  unsupportedVideo: "This video format is not supported. Please upload an MP4 or MOV file.",
  tooLarge: "The file is too large. Please upload a file within the allowed size.",
  alreadySubmitted: "This track has already been submitted.",
};

// Turns any failed request into one sentence for the artist. The server's own message is used for
// validation-type problems (it already says which field and why); the statuses the ticket names get
// fixed wording.
export function trackErrorMessage(error, fallback = MESSAGES.uploadFailed) {
  if (error?.code === "ERR_CANCELED" || axios.isCancel?.(error)) return "Upload cancelled.";
  const response = error?.response;
  if (!response) return fallback;
  const { status, data } = response;
  const code = data?.code;
  if (code === "TRACK_ALREADY_SUBMITTED") return MESSAGES.alreadySubmitted;
  if (code === "FILE_TOO_LARGE" || status === 413) return MESSAGES.tooLarge;
  if (code === "INVALID_FILE_TYPE" || status === 415) {
    if (/artwork|video/i.test(data?.message || "")) return data.message;
    return MESSAGES.unsupportedAudio;
  }
  if (status === 401) return MESSAGES[401];
  if (status === 403) return MESSAGES[403];
  if (status === 404) return MESSAGES[404];
  if (status >= 500 && !["PROCESSING_UNAVAILABLE", "FILE_NOT_REACHABLE"].includes(code)) return MESSAGES[500];
  return data?.message || fallback;
}

// Problems with the file itself say exactly what is wrong; anything else (storage unreachable, service
// down) gets the standard processing message.
const SPECIFIC_PROCESSING_CODES = ["INVALID_AUDIO", "NO_AUDIO_TRACK", "AUDIO_TOO_LONG", "CLIP_TOO_LONG", "AUDIO_QUALITY_TOO_LOW", "AUDIO_CORRUPT", "AUDIO_SILENT"];
export const processingFailureText = (failure) =>
  failure && SPECIFIC_PROCESSING_CODES.includes(failure.code) && failure.message ? failure.message : MESSAGES.processingFailed;

export const processingFailureOf = (error) => ({
  code: error?.response?.data?.code,
  message: error?.response?.data?.message,
});

// Field errors from a 400 (`errors: { field: message }`) so the form can show them next to the field.
export const fieldErrors = (error) => error?.response?.data?.errors || error?.response?.data?.details?.errors || null;

// ---- upload to storage ------------------------------------------------------------------------------------
export class StorageUploadError extends Error {}

// Presign -> PUT, with progress and cancel. Returns what attach-media needs.
export async function uploadToStorage(file, { onProgress, signal } = {}) {
  const renamed = new File([file], safeFileName(file.name), { type: file.type });
  let item;
  try {
    const response = await axios.post(
      `${API_BASE_URL}/aws/generate-presigned-url`,
      { files: [{ fileName: renamed.name, fileType: renamed.type }] },
      { headers: { token: getAuthToken() }, timeout: 20000, signal }
    );
    item = response.data?.directory?.[0];
    if (!response.data?.success || !item) throw new StorageUploadError(MESSAGES.uploadFailed);
  } catch (error) {
    if (error instanceof StorageUploadError || error?.code === "ERR_CANCELED") throw error;
    // Keep status-specific wording (expired session, no permission, ...) from the server.
    throw Object.assign(new StorageUploadError(trackErrorMessage(error)), { cause: error });
  }

  try {
    await axios.put(item.presignedUrl, renamed, {
      headers: { "Content-Type": renamed.type },
      timeout: 0,
      signal,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.min(1, e.loaded / e.total));
      },
    });
  } catch (error) {
    if (error?.code === "ERR_CANCELED") throw error;
    throw new StorageUploadError(MESSAGES.uploadFailed);
  }
  if (onProgress) onProgress(1);
  return { file_name: file.name, file_url: item.fileUrl, storage_key: item.key, mime_type: renamed.type, file_size: file.size };
}

// ---- client-side file checks (same limits the API enforces; the API stays the authority) ------------------
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

const extensionOf = (name) => {
  const dot = String(name || "").lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
};

// Browsers report no or odd MIME types for some audio (for example .flac), so the extension decides and
// a standard MIME type is supplied for the upload.
const EXTENSION_MIME = {
  mp3: "audio/mpeg", wav: "audio/wav", flac: "audio/flac", m4a: "audio/mp4", aac: "audio/aac",
  mp4: "video/mp4", mov: "video/quicktime",
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
};

export function normaliseFile(file) {
  const mime = EXTENSION_MIME[extensionOf(file.name)];
  return mime && file.type !== mime && !file.type ? new File([file], file.name, { type: mime }) : file;
}

export function checkAudioFile(file, rules) {
  const ext = extensionOf(file.name);
  if (!rules.audio.extensions.includes(ext)) return MESSAGES.unsupportedAudio;
  if (file.size > rules.audio.max_bytes) return `${MESSAGES.tooLarge} (maximum ${formatBytes(rules.audio.max_bytes)})`;
  if (file.size === 0) return "The file is empty.";
  return null;
}

// Replacing the audio of a submitted or live track accepts WAV, MP3 or FLAC only.
export const REPLACEMENT_EXTENSIONS = ["wav", "mp3", "flac"];
export function checkReplacementAudio(file, rules) {
  const allowed = rules?.audio?.replacement_extensions || REPLACEMENT_EXTENSIONS;
  if (!allowed.includes(extensionOf(file.name))) return "The replacement audio must be a WAV, MP3 or FLAC file.";
  if (file.size === 0) return "The file is empty.";
  const max = rules?.audio?.max_bytes;
  if (max && file.size > max) return `${MESSAGES.tooLarge} (maximum ${formatBytes(max)})`;
  return null;
}

// Video: MP4 or MOV, within the size limit. Length and the sound track are checked by the server.
export function checkVideoFile(file, rules) {
  const ext = extensionOf(file.name);
  if (!rules.video.extensions.includes(ext)) return MESSAGES.unsupportedVideo;
  if (file.size > rules.video.max_bytes) return `${MESSAGES.tooLarge} (maximum ${formatBytes(rules.video.max_bytes)})`;
  if (file.size === 0) return "The file is empty.";
  return null;
}

export function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("unreadable"));
    };
    image.src = objectUrl;
  });
}

// Resolves to { error } or { width, height }.
export async function checkArtworkFile(file, rules) {
  const ext = extensionOf(file.name);
  if (!rules.artwork.extensions.includes(ext)) return { error: "Artwork must be a JPG or PNG image." };
  if (file.size > rules.artwork.max_bytes) {
    return { error: `The artwork is too large. Please upload an image up to ${formatBytes(rules.artwork.max_bytes)}.` };
  }
  let size;
  try {
    size = await readImageSize(file);
  } catch (e) {
    return { error: "This image could not be read. Please choose another file." };
  }
  if (size.width < rules.artwork.min_width || size.height < rules.artwork.min_height) {
    return { error: `Artwork must be at least ${rules.artwork.min_width} x ${rules.artwork.min_height} pixels (yours is ${size.width} x ${size.height}).` };
  }
  if (rules.artwork.require_square && size.width !== size.height) {
    return { error: `Artwork must be square (yours is ${size.width} x ${size.height}).` };
  }
  return size;
}

export async function sha256Hex(file) {
  try {
    if (!window.crypto?.subtle) return undefined;
    const digest = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    return undefined; // the hash only helps spot duplicates; never block the upload on it
  }
}
